import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from './auth.service';

// ── Exported config (importable by tests) ────────────────────────────────────
/** How often to send a keepalive ping to oauth2-proxy (ms). */
export const PING_INTERVAL_MS = 4 * 60 * 1000;              // 4 min
/**
 * Cross-tab throttle: a tab skips its ping when another tab has already
 * pinged within this fraction of the ping interval (0.8 = last 80%).
 */
export const PING_THROTTLE_FACTOR = 0.8;

// ── Shared localStorage keys ─────────────────────────────────────────────────
export const LS_LAST_PING = 'cb_lastPing';

/**
 * Keeps the oauth2-proxy session alive by sending periodic keepalive
 * pings while a tab is open. Session lifetime is governed solely by the
 * IAM (Keycloak SSO session idle/max) — the app itself never logs the
 * user out.
 *
 * Cross-tab awareness:
 *   Only ONE tab sends the actual HTTP ping per interval. Tabs on the
 *   same origin (including the management-app) coordinate via the shared
 *   `cb_lastPing` localStorage key.
 */
@Injectable({ providedIn: 'root' })
export class SessionActivityService implements OnDestroy {

  // ── Config (use the module-level constants) ────────────────────────────
  private readonly PING_INTERVAL_MS = PING_INTERVAL_MS;

  // ── localStorage keys ──────────────────────────────────────────────────
  private readonly LS_LAST_PING = LS_LAST_PING;

  // ── Internal state ─────────────────────────────────────────────────────
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private started = false;

  constructor(
    private readonly authService: AuthService,
    private readonly ngZone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.stop();
  }

  // ── Public API ─────────────────────────────────────────────────────────

  /**
   * Start the keepalive pings.  Call after the user is authenticated.
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.startPingInterval();
  }

  stop(): void {
    this.started = false;
    this.clearPingInterval();
  }

  // ── Keepalive ping ─────────────────────────────────────────────────────

  private startPingInterval(): void {
    this.clearPingInterval();
    this.ngZone.runOutsideAngular(() => {
      this.pingIntervalId = setInterval(() => this.sendPing(), this.PING_INTERVAL_MS);
    });
  }

  private clearPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  /**
   * Sends a keepalive ping — unless another tab has already pinged
   * recently (coordinated via localStorage).
   */
  private sendPing(): void {
    const now = Date.now();
    try {
      const lastPing = localStorage.getItem(this.LS_LAST_PING);
      if (lastPing && now - parseInt(lastPing, 10) < this.PING_INTERVAL_MS * PING_THROTTLE_FACTOR) {
        return; // Another tab already pinged recently.
      }
      localStorage.setItem(this.LS_LAST_PING, now.toString());
    } catch {
      // localStorage unavailable — ping anyway.
    }

    this.authService.sendSessionPing();
  }
}
